import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ClassPointRewardTier, ItemCard } from '../../types/pointItemNote';
import {
  readClassPointGaugeViewMode,
  writeClassPointGaugeViewMode,
} from '../../utils/classPointGaugeModeStorage';
import { resolveItemCardImageSrc } from '../../utils/itemCardImage';
import {
  clientRectToLayoutRect,
  getLayoutViewportSize,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../../utils/pageZoom';
import ItemCardStackIcon from './ItemCardStackIcon';

type Props = {
  workspaceId: string;
  gradeId: string;
  classTotal: number;
  tiers: ClassPointRewardTier[];
  claimableTierIds: string[];
  onClaimClick: () => void;
};

type HoverState = {
  tierId: string;
  anchorEl: HTMLElement;
};

const VIEW_PAD = 10;

function GaugeMiniCard({ card }: { card: ItemCard }) {
  const src = resolveItemCardImageSrc(card.cardImage);
  return (
    <div className="cp-pin-class-gauge-mini-card">
      <div className="cp-pin-class-gauge-mini-card-icon-wrap">
        <div className="cp-pin-class-gauge-mini-card-icon" aria-hidden>
          {src ? (
            <img src={src} alt="" />
          ) : (
            <ItemCardStackIcon size={28} />
          )}
        </div>
      </div>
      <p className="cp-pin-class-gauge-mini-card-name">{card.name || '(이름 없음)'}</p>
      <p className="cp-pin-class-gauge-mini-card-memo">{card.memo?.trim() || '\u00A0'}</p>
    </div>
  );
}

const ClassPointGaugeBar: React.FC<Props> = ({
  workspaceId,
  gradeId,
  classTotal,
  tiers,
  claimableTierIds,
  onClaimClick,
}) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  const [placement, setPlacement] = useState<'above' | 'below'>('above');
  const [panelPos, setPanelPos] = useState<{ left: number; top: number } | null>(null);
  /** true면 다음 학급 보상 포인트를 끝으로 확대 */
  const [nextRewardMode, setNextRewardMode] = useState(() =>
    readClassPointGaugeViewMode(workspaceId, gradeId) === 'next'
  );
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    setNextRewardMode(readClassPointGaugeViewMode(workspaceId, gradeId) === 'next');
  }, [workspaceId, gradeId]);

  const setGaugeMode = (next: boolean) => {
    setNextRewardMode(next);
    writeClassPointGaugeViewMode(workspaceId, gradeId, next ? 'next' : 'full');
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openHover = (next: HoverState) => {
    clearCloseTimer();
    setHover(next);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setHover(null);
      closeTimerRef.current = null;
    }, 160);
  };

  useEffect(() => () => clearCloseTimer(), []);

  const sorted = useMemo(
    () =>
      [...tiers]
        .filter((t) => t.thresholdPoints > 0)
        .sort((a, b) => a.thresholdPoints - b.thresholdPoints || a.id.localeCompare(b.id)),
    [tiers]
  );

  const fullMax = useMemo(() => {
    const top = sorted[sorted.length - 1]?.thresholdPoints ?? 0;
    return Math.max(top, classTotal, 1);
  }, [sorted, classTotal]);

  const nextRewardTier = useMemo(
    () => sorted.find((t) => t.thresholdPoints > classTotal) ?? null,
    [sorted, classTotal]
  );

  const gaugeMax = useMemo(() => {
    if (!nextRewardMode) return fullMax;
    if (nextRewardTier) return Math.max(nextRewardTier.thresholdPoints, 1);
    // 다음 보상이 없으면 현재 점수까지(가득 찬 상태)로 표시
    return Math.max(classTotal, 1);
  }, [nextRewardMode, fullMax, nextRewardTier, classTotal]);

  const visibleMarkers = useMemo(
    () => sorted.filter((t) => t.thresholdPoints <= gaugeMax),
    [sorted, gaugeMax]
  );

  const fillPct = Math.max(0, Math.min(100, (classTotal / gaugeMax) * 100));
  const claimable = claimableTierIds.length > 0;
  const hoverTier = hover ? sorted.find((t) => t.id === hover.tierId) ?? null : null;

  useLayoutEffect(() => {
    if (!hover || !panelRef.current) {
      setPanelPos(null);
      return;
    }
    const panel = panelRef.current;
    const panelLayout = clientRectToLayoutRect(panel.getBoundingClientRect());
    const anchor = clientRectToLayoutRect(hover.anchorEl.getBoundingClientRect());
    const { width: vw, height: vh } = getLayoutViewportSize();
    const spaceAbove = anchor.top - VIEW_PAD;
    const spaceBelow = vh - anchor.bottom - VIEW_PAD;
    const preferAbove = spaceAbove >= panelLayout.height + 8 || spaceAbove >= spaceBelow;
    const nextPlacement = preferAbove ? 'above' : 'below';
    setPlacement(nextPlacement);

    let left = anchor.left + anchor.width / 2 - panelLayout.width / 2;
    left = Math.max(VIEW_PAD, Math.min(left, vw - panelLayout.width - VIEW_PAD));

    let top = preferAbove
      ? anchor.top - panelLayout.height - 10
      : anchor.bottom + 10;
    top = Math.max(VIEW_PAD, Math.min(top, vh - panelLayout.height - VIEW_PAD));

    setPanelPos({ left, top });
  }, [hover, hoverTier?.cards.length]);

  useEffect(() => {
    if (!hover) return;
    const clear = () => setHover(null);
    const onScroll = () => clear();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', clear);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, clear);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', clear);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, clear);
    };
  }, [hover]);

  // 다음 보상이 사라지면(도달·설정 변경) 확대 모드 해제
  useEffect(() => {
    if (nextRewardMode && !nextRewardTier && classTotal >= fullMax) {
      setNextRewardMode(false);
      writeClassPointGaugeViewMode(workspaceId, gradeId, 'full');
    }
  }, [nextRewardMode, nextRewardTier, classTotal, fullMax, workspaceId, gradeId]);

  return (
    <div
      className={`cp-pin-class-gauge${claimable ? ' is-claimable' : ''}${
        nextRewardMode ? ' is-next-mode' : ''
      }`}
    >
      <div
        className={`cp-pin-class-gauge-track${claimable ? ' is-sparkle' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => setGaugeMode(!nextRewardMode)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setGaugeMode(!nextRewardMode);
          }
        }}
        title={
          nextRewardMode
            ? '전체 게이지로 보기'
            : nextRewardTier
              ? `다음 보상(${nextRewardTier.thresholdPoints.toLocaleString('ko-KR')}점)까지 보기`
              : '전체 게이지로 보기'
        }
        aria-pressed={nextRewardMode}
        aria-label={
          nextRewardMode
            ? `학급 포인트 ${classTotal.toLocaleString('ko-KR')}점, 다음 보상까지 보기 중. 클릭하면 전체 보기`
            : `학급 포인트 ${classTotal.toLocaleString('ko-KR')}점. 클릭하면 다음 보상까지 보기`
        }
      >
        <div
          className="cp-pin-class-gauge-fill"
          style={{ width: `${fillPct}%` }}
          aria-hidden
        />
        <span className="cp-pin-class-gauge-score">
          <span className="cp-pin-class-gauge-label">학급 포인트</span>
          <span className="cp-pin-class-gauge-total">
            {classTotal.toLocaleString('ko-KR')}
            <span className="cp-pin-class-gauge-total-unit">점</span>
          </span>
          {claimable ? (
            <button
              type="button"
              className="cp-pin-class-gauge-claim-chip"
              onClick={(e) => {
                e.stopPropagation();
                onClaimClick();
              }}
            >
              보상 받기
            </button>
          ) : null}
        </span>
        {visibleMarkers.map((tier) => {
          const left = Math.min(100, (tier.thresholdPoints / gaugeMax) * 100);
          const isHover = hover?.tierId === tier.id;
          return (
            <div
              key={tier.id}
              className={`cp-pin-class-gauge-marker${
                claimableTierIds.includes(tier.id) ? ' is-claimable' : ''
              }${isHover ? ' is-hover' : ''}`}
              style={{ left: `${left}%` }}
              onMouseEnter={(e) => {
                if (tier.cards.length === 0) return;
                openHover({
                  tierId: tier.id,
                  anchorEl: e.currentTarget,
                });
              }}
              onMouseLeave={scheduleClose}
            >
              <span className="cp-pin-class-gauge-marker-line" aria-hidden />
              <span className="cp-pin-class-gauge-marker-label">
                {tier.thresholdPoints.toLocaleString('ko-KR')}
              </span>
            </div>
          );
        })}
      </div>

      {hover && hoverTier && hoverTier.cards.length > 0
        ? createPortal(
            <div
              ref={panelRef}
              className={`cp-pin-class-gauge-hover-panel is-portal is-${placement}${
                panelPos ? ' is-placed' : ''
              }`}
              role="tooltip"
              style={
                panelPos
                  ? { left: panelPos.left, top: panelPos.top }
                  : (() => {
                      const a = clientRectToLayoutRect(
                        hover.anchorEl.getBoundingClientRect()
                      );
                      return {
                        left: a.left + a.width / 2,
                        top: a.top,
                        transform: 'translate(-50%, calc(-100% - 10px))',
                        visibility: 'hidden' as const,
                      };
                    })()
              }
              onMouseEnter={() => {
                clearCloseTimer();
                setHover(hover);
              }}
              onMouseLeave={scheduleClose}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cp-pin-class-gauge-hover-cards">
                {hoverTier.cards.map((card) => (
                  <GaugeMiniCard key={card.itemCardId} card={card} />
                ))}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ClassPointGaugeBar;
